import { useSelector } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import isEmpty from 'lodash/isEmpty'

// Selectors
import {
  selectCurrentMarketMetadata,
  selectCurrentZipcode,
  selectCurrentDataPriceMin,
  selectCurrentDataPriceMax,
  selectCurrentDataRepresentsQuartile,
  selectCurrentDataRepresentsMutliFamily,
} from '../../../../store/selectors/marketRecord'

// Components
import Format from '../../Format'
import SectionWrapper from '../../SectionWrapper'
import Text from '../../Text'

// Styles
import baseStyles from '../MarketSpotlight.module.scss'
import styles from './MarketFacts.module.scss'

const NumberWrapper = props => (
  <Text
    size='l'
    element='div'
    variant='neutralCool'
    variantCount='900'
    className={styles.wrapper__item__number}
    {...props}
  />
)

const Label = props => (
  <Text
    size='xs'
    element='div'
    variant='neutralCool'
    variantCount='700'
    {...props}
  />
)

const typeMessages = defineMessages({
  single: {
    id: 'MarketSpotlight.marketFacts.type.single',
    defaultMessage: 'Homes',
  },
  multi: {
    id: 'MarketSpotlight.marketFacts.type.multi',
    defaultMessage: 'Condos',
  },
})

export const MarketFacts = () => {
  const metadata = useSelector(selectCurrentMarketMetadata)
  const { data = {} } = metadata
  const { bedsMean, bathsMean, sqftMedian } = data

  const zipcode = useSelector(selectCurrentZipcode)
  const priceRangeMin = useSelector(selectCurrentDataPriceMin) || 0
  const priceRangeMax = useSelector(selectCurrentDataPriceMax) || 0
  const forQuartile = useSelector(selectCurrentDataRepresentsQuartile)
  const forMultiFamily = useSelector(selectCurrentDataRepresentsMutliFamily)

  const intl = useIntl()
  const homeTypeCopy = forMultiFamily
    ? intl.formatMessage(typeMessages.multi)
    : intl.formatMessage(typeMessages.single)

  if (isEmpty(data)) {
    return null
  }

  return (
    <SectionWrapper
      className={baseStyles.section}
      data-qa='market-facts-section'
    >
      <Text
        element='h4'
        size='l'
        align='center'
        paragraph
        variant='neutralCool'
        variantCount='900'
        className={styles.text}
      >
        {forQuartile ? (
          <FormattedMessage
            id='MarketSpotlight.marketFacts.quartile'
            defaultMessage='{type} around {range} have:'
            values={{
              type: homeTypeCopy,
              range: (
                <Text element='span' variant='primary'>
                  <Format.Currency value={priceRangeMin} abbreviate />
                  -
                  <Format.Currency value={priceRangeMax} abbreviate />
                </Text>
              ),
            }}
          />
        ) : (
          <FormattedMessage
            id='MarketSpotlight.marketFacts.all'
            defaultMessage='{type} in {zipcode} on average have:'
            values={{
              type: homeTypeCopy,
              zipcode: (
                <Text element='span' variant='primary'>
                  {zipcode}
                </Text>
              ),
            }}
          />
        )}
      </Text>
      <div className={styles.wrapper}>
        <div data-qa='market-facts-beds-mean' className={styles.wrapper__item}>
          <NumberWrapper>
            <Format.Number value={bedsMean} format='0.0' />
          </NumberWrapper>
          <Label>
            <FormattedMessage
              id='MarketSpotlight.marketFacts.beds'
              defaultMessage='Beds'
            />
          </Label>
        </div>
        <div data-qa='market-facts-baths-mean' className={styles.wrapper__item}>
          <NumberWrapper>
            <Format.Number value={bathsMean} format='0.0' />
          </NumberWrapper>
          <Label>
            <FormattedMessage
              id='MarketSpotlight.marketFacts.avgBaths'
              defaultMessage='Baths'
            />
          </Label>
        </div>
        <div
          data-qa='market-facts-sqft-median'
          className={styles.wrapper__item}
        >
          <NumberWrapper>
            <Format.Number value={sqftMedian} format='0,0' />
          </NumberWrapper>
          <Label>
            <FormattedMessage
              id='ZipCodeDetail.label.avgSqft'
              defaultMessage='Sqft'
            />
          </Label>
        </div>
      </div>
    </SectionWrapper>
  )
}
